import React, { FCC, useContext } from 'react'

import { Box, Dropdown, ErrorMessage, ModalContext } from '@sketch/components'
import {
  ShareListItemFragment,
  TrashedShareFragment,
  useGetShareWorkspaceQuery,
  useGetShareQuery,
} from '@sketch/gql-types'
import { DeleteSharePermanentlyModal } from '../DeleteSharePermanentlyModal'
import { MoveStorageItemModal } from '../../../storageItems'

import { canRestoreShare } from '../../utils'

import { useGetTrashedShareDropdownOptions } from '../../operations/useGetTrashedShareDropdownOptions'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-17'
import { ReactComponent as RestoreIcon } from '@sketch/icons/arrow-counterclockwise-17'
import { ReactComponent as TrashIcon } from '@sketch/icons/trash-empty-17'

// ShareListItemFragment should be removed once BE returns the newer types in the getTrash
// https://github.com/sketch-hq/Cloud/issues/11226
export interface ShareCommonForTrashedActionsDropdown
  extends Pick<
    ShareListItemFragment | TrashedShareFragment,
    'name' | 'identifier' | 'project'
  > {
  version: {
    shortId: string
    identifier: string
  } | null
}

export type ShareForTrashedActionsDropdown =
  | (ShareCommonForTrashedActionsDropdown &
      Pick<ShareListItemFragment, '__typename'>)
  | (ShareCommonForTrashedActionsDropdown &
      Pick<TrashedShareFragment, '__typename' | 'workspace'>)

const useShareWorkspace = (share: ShareForTrashedActionsDropdown) => {
  const existingWorkspace =
    share.__typename === 'TrashedShare' ? share.workspace : null

  /**
   * TODO: use dedicated query to get trashed documents inside of trashed project
   *  see: https://github.com/sketch-hq/Cloud/issues/12873
   */
  const result = useGetShareWorkspaceQuery({
    variables: { shareIdentifier: share.identifier },
    skip: !!existingWorkspace,
  })

  const workspace = existingWorkspace || result.data?.share?.workspace

  return Object.assign(result, { workspace })
}

interface ShareActionsDropdownTrashedProps {
  share: ShareForTrashedActionsDropdown
}

export const ShareActionsDropdownTrashed: FCC<
  ShareActionsDropdownTrashedProps
> = props => {
  const { showModal } = useContext(ModalContext)
  const { share } = props

  const dropdownOptions = useGetTrashedShareDropdownOptions({
    share: props.share,
    version: share.version!,
  })

  const shareWorkspace = useShareWorkspace(share)

  // Preload share details, we don't need them in the dropdown, but we need them
  // in the Project Picker modal, so we preload for faster user experience.
  useGetShareQuery({ variables: { shortId: share.identifier } })

  if (dropdownOptions.error || shareWorkspace.error) {
    return <ErrorMessage.Generic />
  }

  if (dropdownOptions.loading || shareWorkspace.loading)
    return (
      <>
        <Dropdown.Skeleton count={2} />
        <Dropdown.Divider />
        <Dropdown.Skeleton />
      </>
    )

  const isUserAdmin = shareWorkspace.workspace?.userRole === 'ADMIN'

  const { download, userAccessLevel } = dropdownOptions.data

  if (!(userAccessLevel !== 'VIEW' || isUserAdmin)) {
    return <ErrorMessage.Generic />
  }

  const shareName = share.name
  const shareIdentifier = share.identifier
  const shareProject = share.project
  const isInDraftsProject = share.project?.projectType === 'PERSONAL_DRAFTS'
  const userCanDeletePermanently = isUserAdmin || isInDraftsProject

  return (
    <Box width={200}>
      <Dropdown.Header>Document</Dropdown.Header>
      {download.isAvailable && (
        <Dropdown.Download icon={<DownloadIcon />} href={download.url}>
          Download
        </Dropdown.Download>
      )}

      {(canRestoreShare({ userAccessLevel }) || isUserAdmin) && (
        <Dropdown.Item
          icon={<RestoreIcon />}
          onClick={() =>
            showModal(MoveStorageItemModal, {
              items: [{ __typename: 'Share', identifier: shareIdentifier }],
              workspaceIdentifier: shareWorkspace.workspace?.identifier!,
              parentProjectIdentifier: shareProject?.identifier || null,
              originType: 'stashed',
              action: 'restore',
            })
          }
        >
          Restore&hellip;
        </Dropdown.Item>
      )}

      {userCanDeletePermanently && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item
            icon={<TrashIcon />}
            intent="negative"
            onClick={() =>
              showModal(DeleteSharePermanentlyModal, {
                name: shareName,
                shareIdentifier,
                workspaceId: shareWorkspace.workspace?.identifier!,
              })
            }
          >
            Delete Permanently&hellip;
          </Dropdown.Item>
        </>
      )}
    </Box>
  )
}
